/**
 * Tasks (Phase 5.3): the card in the thread, the create form, the detail with
 * sharing to more rooms, and lists. A task is one object: every card and list
 * reads it from the store, which task.updated events keep current.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, ApiError } from "../lib/api";
import {
  approveTask,
  confirmTask,
  createTask,
  keepTasks,
  linkTaskGithub,
  refreshTaskGithub,
  sendBackTask,
  shareTask,
  STATUS_LABEL,
  unshareTask,
  updateTask,
  useStore,
  type CiState,
  type Message,
  type Person,
  type Task,
  type TaskStatus,
} from "../lib/store";
import { Avatar, ErrorNote, Modal } from "./ui";

const STATUSES: TaskStatus[] = ["todo", "doing", "review", "done", "cancelled"];
const today = () => new Date().toISOString().slice(0, 10);
const dueLabel = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric" });

export function StatusSelect({ task }: { task: Task }) {
  const [error, setError] = useState(false);
  return (
    <select
      className={`status-pill s-${task.status}${error ? " failed" : ""}`}
      value={task.status}
      disabled={!task.canEdit}
      aria-label={`Status of ${task.title}`}
      onChange={async (e) => {
        setError(false);
        await updateTask(task.id, { status: e.target.value as TaskStatus }).catch(() => setError(true));
      }}
    >
      {STATUSES.map((s) => (
        <option key={s} value={s}>
          {STATUS_LABEL[s]}
        </option>
      ))}
    </select>
  );
}

function Due({ task }: { task: Task }) {
  if (!task.dueDate) return null;
  const late = task.dueDate < today() && task.status !== "done" && task.status !== "cancelled";
  return <span className={`due${late ? " late" : ""}`}>{late ? "late, " : ""}due {dueLabel(task.dueDate)}</span>;
}

export function CiDot({ ci }: { ci: CiState }) {
  if (!ci) return null;
  const label = ci === "success" ? "CI passed" : ci === "failure" ? "CI failed" : "CI running";
  return <span className={`ci-dot ci-${ci}`} title={label} aria-label={label} />;
}

const FLOW_LABEL: Record<NonNullable<Task["flow"]>, string> = {
  approved: "Approved",
  building: "Worker building",
  pr_open: "Pull request open",
  reviewing: "Being reviewed",
  reviewed: "Reviewed",
  confirmed: "Confirmed",
  merged: "Merged",
  live: "Live",
  manual: "Merge it yourself",
  failed: "Worker stopped",
};

/** The coordinator's chain on a code task: where it is, and the owner's Approve or Confirm. */
export function TaskFlow({ task, full }: { task: Task; full?: boolean }) {
  const owner = useStore((s) => Boolean(s.boot?.me.isOwner));
  const project = useStore((s) => (task.projectId ? s.boot?.projects.find((p) => p.id === task.projectId) : undefined));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = task.status !== "done" && task.status !== "cancelled";
  const canApprove = owner && project?.workersEnabled && open && (!task.flow || task.flow === "failed");
  const canConfirm = owner && (task.flow === "reviewed" || task.flow === "pr_open");
  const canSendBack = canConfirm && Boolean(project?.workersEnabled && task.githubPr);
  const [sending, setSending] = useState(false);
  if (!task.flow && !canApprove) return null;
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`task-flow f-${task.flow ?? "none"}`}>
      {task.flow && <span className="flow-chip">{FLOW_LABEL[task.flow]}</span>}
      {(full || task.flow === "failed" || task.flow === "manual" || task.flow === "reviewed") && task.flowNote && <small className="flow-note">{task.flowNote}</small>}
      {canApprove && (
        <button className="btn primary small" type="button" disabled={busy} onClick={() => void run(() => approveTask(task.id))} title="A Claude Code worker builds it on GitHub, on your Max plan">
          {task.flow === "failed" ? "Approve again" : "Approve"}
        </button>
      )}
      {canConfirm && (
        <button className="btn primary small" type="button" disabled={busy} onClick={() => void run(() => confirmTask(task.id))} title={project?.deliveryMode === "automatic" ? "Merge it: the project's deploy makes it live" : "Manual project: you merge and deploy"}>
          Confirm
        </button>
      )}
      {canSendBack && (
        <button className="btn ghost small" type="button" disabled={busy} onClick={() => setSending(true)} title="The worker fixes the review's points on the same pull request">
          Send back to the worker
        </button>
      )}
      {error && <small className="flow-error">{error}</small>}
      {sending && <SendBackModal task={task} onClose={() => setSending(false)} />}
    </div>
  );
}

/** The owner sends the pull request back to the worker, with the review and an optional note. */
function SendBackModal({ task, onClose }: { task: Task; onClose: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title="Send back to the worker" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await sendBackTask(task.id, note);
            onClose();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not send it back.");
            setBusy(false);
          }
        }}
      >
        <ErrorNote error={error} />
        <p className="muted-text">
          A new worker round on pull request #{task.githubPr}: it gets the task, ChatGPT's review and your note, commits the fixes on the same branch, and ChatGPT reviews again. It runs on your Max plan.
        </p>
        <label className="field">
          <span>Anything to add (optional)</span>
          <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="For example: ignore the preview point; keep the hero shorter." />
        </label>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy}>
            Send back
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** "PR #12 merged" or "Issue #7 open", for cards and rows. */
export function GithubChip({ task }: { task: Task }) {
  const g = task.github;
  if (g?.pr && task.githubPr) {
    const state = g.pr.merged ? "merged" : g.pr.state === "closed" ? "closed" : g.pr.draft ? "draft" : "open";
    return (
      <a className={`gh-chip gh-${state}`} href={g.pr.url} target="_blank" rel="noreferrer" title={g.pr.title}>
        PR #{task.githubPr} {state} <CiDot ci={g.pr.ci} />
      </a>
    );
  }
  if (g?.issue && task.githubIssue)
    return (
      <a className={`gh-chip gh-${g.issue.state}`} href={g.issue.url} target="_blank" rel="noreferrer" title={g.issue.title}>
        Issue #{task.githubIssue} {g.issue.state}
      </a>
    );
  if (task.githubBranch) return <span className="gh-chip">branch {task.githubBranch}</span>;
  return null;
}

/** One line for lists: status, title, owner, due, rooms. */
export function TaskRow({ task, hereRoomId }: { task: Task; hereRoomId?: string }) {
  const owner = useStore((s) => (task.ownerId ? s.boot?.people.find((p) => p.id === task.ownerId) : undefined));
  const [open, setOpen] = useState(false);
  const elsewhere = task.rooms.filter((r) => r.id !== hereRoomId);
  return (
    <li className={`task-row s-${task.status}`}>
      <StatusSelect task={task} />
      <div className="task-main">
        <button type="button" className="task-title" onClick={() => setOpen(true)}>
          {task.title}
        </button>
        <small>
          {owner ? owner.name : "No owner"}
          {task.dueDate && (
            <>
              {" · "}
              <Due task={task} />
            </>
          )}
          {elsewhere.length > 0 && <> · {hereRoomId ? "also in " : "in "}{elsewhere.map((r) => `#${r.name}`).join(", ")}</>}
        </small>
        <GithubChip task={task} />
      </div>
      {open && <TaskDetail taskId={task.id} onClose={() => setOpen(false)} />}
    </li>
  );
}

/** The card a task message shows in the thread, always the task's current state. */
export function TaskCard({ message, roomId }: { message: Message; roomId: string }) {
  const task = useStore((s) => (message.taskId ? s.tasks[message.taskId] : undefined));
  const owner = useStore((s) => (task?.ownerId ? s.boot?.people.find((p) => p.id === task.ownerId) : undefined));
  const [open, setOpen] = useState(false);
  if (!task) return <p className="task-card gone">{message.body}</p>;
  const elsewhere = task.rooms.filter((r) => r.id !== roomId);
  return (
    <div className={`task-card s-${task.status}`}>
      <div className="task-card-top">
        <StatusSelect task={task} />
        <button type="button" className="task-title" onClick={() => setOpen(true)}>
          {task.title}
        </button>
      </div>
      <div className="task-card-meta">
        {owner ? (
          <span className="who">
            <Avatar person={owner} size={18} /> {owner.name}
          </span>
        ) : (
          <span>No owner</span>
        )}
        <Due task={task} />
        {task.projectName && task.projectId && <Link to={`/p/${task.projectId}`}>{task.projectName}</Link>}
        {elsewhere.length > 0 && <span>also in {elsewhere.map((r) => `#${r.name}`).join(", ")}</span>}
        <GithubChip task={task} />
      </div>
      <TaskFlow task={task} />
      {open && <TaskDetail taskId={task.id} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** People and agents who can own a task here: the room's members, or everyone when no room. */
function useOwners(roomIds: string[]): Person[] {
  const boot = useStore((s) => s.boot)!;
  return useMemo(() => {
    const ids = new Set(boot.rooms.filter((r) => roomIds.includes(r.id)).flatMap((r) => r.members.map((m) => m.userId)));
    return boot.people.filter((p) => !roomIds.length || ids.has(p.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [boot, roomIds.join(",")]);
}

function OwnerField({ value, onChange, owners }: { value: string; onChange: (v: string) => void; owners: Person[] }) {
  return (
    <label className="field">
      <span>Owner</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">No owner</option>
        {owners.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.kind === "agent" ? " (agent)" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

/** New task in a room, by hand or from a message (title prefilled from it). */
export function NewTaskModal({ roomId, from, onClose }: { roomId: string; from?: Message; onClose: () => void }) {
  const me = useStore((s) => s.boot!.me.id);
  const owners = useOwners([roomId]);
  const firstLine = from ? (from.body.split("\n").find((l) => l.trim() && !/^>( |$)/.test(l)) ?? from.body).trim().slice(0, 200) : "";
  const [title, setTitle] = useState(firstLine);
  const [notes, setNotes] = useState("");
  const [ownerId, setOwnerId] = useState(me);
  const [dueDate, setDueDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createTask(roomId, { title, notes, ownerId: ownerId || null, dueDate: dueDate || null, fromMessageId: from?.id });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the task.");
      setBusy(false);
    }
  }
  return (
    <Modal title="New task" onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <label className="field">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required autoFocus />
        </label>
        <OwnerField value={ownerId} onChange={setOwnerId} owners={owners} />
        <label className="field">
          <span>Due date (optional)</span>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
        <label className="field">
          <span>Notes (optional)</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={8000} />
        </label>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy || !title.trim()}>
            {busy ? "Creating" : "Create task"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Everything about one task: edit it, see and change the rooms it is in. */
export function TaskDetail({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const task = useStore((s) => s.tasks[taskId]);
  const boot = useStore((s) => s.boot)!;
  const owners = useOwners(task?.rooms.map((r) => r.id) ?? []);
  const [title, setTitle] = useState(task?.title ?? "");
  const [notes, setNotes] = useState(task?.notes ?? "");
  const [ownerId, setOwnerId] = useState(task?.ownerId ?? "");
  const [dueDate, setDueDate] = useState(task?.dueDate ?? "");
  const [shareTo, setShareTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  if (!task) return null;
  const canShareTo = boot.rooms.filter((r) => r.kind === "room" && r.isMember && !task.rooms.some((x) => x.id === r.id));
  const dirty = title !== task.title || notes !== task.notes || (ownerId || null) !== task.ownerId || (dueDate || null) !== task.dueDate;
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
      return false;
    }
  };
  return (
    <Modal title="Task" onClose={onClose} wide>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (await run(() => updateTask(task.id, { title, notes, ownerId: ownerId || null, dueDate: dueDate || null }))) {
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
          }
        }}
      >
        <ErrorNote error={error} />
        <div className="task-detail-head">
          <StatusSelect task={task} />
          {task.projectName && task.projectId && (
            <Link to={`/p/${task.projectId}`} onClick={onClose}>
              {task.projectName}
            </Link>
          )}
        </div>
        <label className="field">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required disabled={!task.canEdit} />
        </label>
        <OwnerField value={ownerId} onChange={setOwnerId} owners={owners} />
        <label className="field">
          <span>Due date</span>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={!task.canEdit} />
        </label>
        <label className="field">
          <span>Notes</span>
          <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={8000} disabled={!task.canEdit} />
        </label>
        {task.canEdit && (
          <div className="actions">
            {saved && <span className="chip ok">saved</span>}
            <button className="btn primary small" type="submit" disabled={!dirty || !title.trim()}>
              Save
            </button>
          </div>
        )}
        {task.projectId && (
          <div className="field">
            <span>Coding worker (plan.md: you approve, a worker builds it, ChatGPT reviews, you confirm)</span>
            <TaskFlow task={task} full />
          </div>
        )}
        {task.projectId && <TaskGithubSection task={task} onError={setError} />}
        <div className="field">
          <span>In these rooms (one task, the same everywhere)</span>
          <ul className="rows">
            {task.rooms.map((r) => (
              <li key={r.id}>
                <div>
                  <Link to={`/r/${r.id}`} onClick={onClose}>
                    #{r.name}
                  </Link>
                </div>
                {task.canEdit && task.rooms.length > 1 && (
                  <button className="linkish" type="button" onClick={() => void run(() => unshareTask(task.id, r.id))}>
                    Remove from room
                  </button>
                )}
              </li>
            ))}
          </ul>
          {task.canEdit && canShareTo.length > 0 && (
            <div className="key-form">
              <select value={shareTo} onChange={(e) => setShareTo(e.target.value)} aria-label="Share to another room">
                <option value="">Share to another room</option>
                {canShareTo.map((r) => (
                  <option key={r.id} value={r.id}>
                    #{r.name}
                  </option>
                ))}
              </select>
              <button className="btn ghost small" type="button" disabled={!shareTo} onClick={async () => (await run(() => shareTask(task.id, shareTo))) && setShareTo("")}>
                Share
              </button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

/** Links to the project repo: the status follows the pull request or issue. */
function TaskGithubSection({ task, onError }: { task: Task; onError: (e: string | null) => void }) {
  const [link, setLink] = useState("");
  const [branch, setBranch] = useState(task.githubBranch ?? "");
  const [busy, setBusy] = useState(false);
  const g = task.github;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    onError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "GitHub did not answer.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="field">
      <span>GitHub (the status follows: open pull request In review, merged Done, closed issue Done)</span>
      <ul className="rows">
        {task.githubPr && (
          <li>
            <div>
              <a href={g?.pr?.url ?? "#"} target="_blank" rel="noreferrer">
                Pull request #{task.githubPr}
              </a>
              <small>
                {g?.pr ? `${g.pr.title} · ${g.pr.merged ? "merged" : g.pr.draft ? "draft" : g.pr.state}` : "not read yet"} {g?.pr && <CiDot ci={g.pr.ci} />}
              </small>
            </div>
            {task.canEdit && (
              <button className="linkish" type="button" disabled={busy} onClick={() => void run(() => linkTaskGithub(task.id, { unlink: "pr" }))}>
                Unlink
              </button>
            )}
          </li>
        )}
        {task.githubIssue && (
          <li>
            <div>
              <a href={g?.issue?.url ?? "#"} target="_blank" rel="noreferrer">
                Issue #{task.githubIssue}
              </a>
              <small>{g?.issue ? `${g.issue.title} · ${g.issue.state}` : "not read yet"}</small>
            </div>
            {task.canEdit && (
              <button className="linkish" type="button" disabled={busy} onClick={() => void run(() => linkTaskGithub(task.id, { unlink: "issue" }))}>
                Unlink
              </button>
            )}
          </li>
        )}
      </ul>
      {task.canEdit && (
        <>
          <div className="key-form">
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Issue or pull request: its link or #number" aria-label="GitHub issue or pull request" />
            <button className="btn ghost small" type="button" disabled={busy || !link.trim()} onClick={async () => (await run(() => linkTaskGithub(task.id, { link }))) && setLink("")}>
              Link
            </button>
          </div>
          <div className="key-form">
            <input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="Branch (its pull request links itself)" aria-label="Branch" />
            <button className="btn ghost small" type="button" disabled={busy || branch === (task.githubBranch ?? "")} onClick={() => void run(() => linkTaskGithub(task.id, { branch: branch.trim() || null }))}>
              Save
            </button>
          </div>
          {(task.githubPr || task.githubIssue || task.githubBranch) && (
            <button className="linkish" type="button" disabled={busy} onClick={() => void run(() => refreshTaskGithub(task.id))}>
              Check GitHub again{g?.checkedAt ? ` (last ${new Date(g.checkedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})` : ""}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** A list from one endpoint, reloaded when any task changes. */
export function useTaskList(url: string): { list: Task[] | null; error: string | null } {
  const version = useStore((s) => s.tasksVersion);
  const tasks = useStore((s) => s.tasks);
  const [ids, setIds] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ tasks: Task[] }>(url).then(
      (r) => (keepTasks(r.tasks), setIds(r.tasks.map((t) => t.id))),
      (err) => setError(err instanceof ApiError ? err.message : "Could not load the tasks."),
    );
  }, [url, version]);
  const list = useMemo(() => (ids ? (ids.map((id) => tasks[id]).filter(Boolean) as Task[]) : null), [ids, tasks]);
  return { list, error };
}

const ORDER: Record<TaskStatus, number> = { doing: 0, review: 1, todo: 2, done: 3, cancelled: 4 };
export const byUrgency = (a: Task, b: Task) => ORDER[a.status] - ORDER[b.status] || (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") || b.updatedAt.localeCompare(a.updatedAt);
